import { useEffect } from "react";
import { Box, Button, Flex, Heading, SimpleGrid } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import {
  AddressInput,
  BrandPatch,
  BrandCompanyContactInput,
  PhoneNumberInput,
} from "__generated__/gql/graphql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import Form from "components/Shared/Forms/Form";
import AddressFieldset, {
  AddressFieldsetType,
  getHasAddressData,
} from "components/Shared/Forms/AddressFieldset";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import PhoneInput from "components/Shared/Forms/PhoneInput";
import CompanyAttributeSelect from "components/Shared/Select/CompanyAttributeSelect";
import UrlInput from "components/Shared/Forms/UrlInput";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import {
  getPostgraphileErrorMatchersForFields,
  makePostgraphileErrorProcessor,
} from "utils/PostgraphileErrorExtensions";
import { assertBrandId } from "utils/assertions";
import * as brandSchemaFields from "utils/schemas/brand";
import * as brandCompanyContactSchemaFields from "utils/schemas/brandCompanyContact";

const fragment = gql(/* GraphQL */ `
  fragment BrandSettingsCompanyInformationEditFields on Brand {
    id
    legalName
    website
    headquartersContact {
      id
      email
      addressId
      phoneNumberId
      address {
        id
        line1
        line2
        city
        state
        zip
      }
      phoneNumber {
        id
        phone
      }
    }
    billingContact {
      id
      name
      email
      addressId
      phoneNumberId
      address {
        id
        line1
        line2
        city
        state
        zip
      }
      phoneNumber {
        id
        phone
      }
    }
    ordersLogisticsContact {
      id
      name
      email
      addressId
      phoneNumberId
      address {
        id
        line1
        line2
        city
        state
        zip
      }
      phoneNumber {
        id
        phone
      }
    }
    companyAttributesList(orderBy: NAME_ASC) {
      id
      ...AttributeSelectOption
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation UpdateBrandCompanyInformation(
    $brandId: UUID!
    $brandPatch: BrandPatch!
    $addresses: [AddressInput!] = []
    $phoneNumbers: [PhoneNumberInput!] = []
    $brandCompanyContacts: [BrandCompanyContactInput!] = []
    $companyAttributeIds: [UUID!] = []
  ) {
    upsertAddressesById(input: { addresses: $addresses }) {
      addresses {
        id
        ...AddressComponentFields
      }
    }
    upsertPhoneNumbersById(input: { phoneNumbers: $phoneNumbers }) {
      phoneNumbers {
        id
      }
    }
    upsertBrandCompanyContactsById(
      input: { brandCompanyContacts: $brandCompanyContacts }
    ) {
      brandCompanyContacts {
        id
      }
    }
    setCompanyAttributesForBrand(
      input: { brandId: $brandId, attributeIds: $companyAttributeIds }
    ) {
      brand {
        id
      }
    }
    updateBrand(input: { id: $brandId, patch: $brandPatch }) {
      brand {
        id
        ...BrandSettingsCompanyInformationFields
        ...BrandSettingsCompanyInformationEditFields
      }
    }
  }
`);

type ContactInputs = {
  email: string | null;
  phone: string | null;
  address: AddressFieldsetType;
};

type NamedContactInputs = ContactInputs & {
  name: string | null;
};

const BRAND_FIELDS = ["legalName", "website"] as const;

type PrimitiveInputs = Required<
  Pick<BrandPatch, (typeof BRAND_FIELDS)[number]>
>;

type Inputs = PrimitiveInputs & {
  companyAttributeIds: UUID[];
  headquartersContact: ContactInputs;
  billingContact: NamedContactInputs;
  ordersLogisticsContact: NamedContactInputs;
};

type Brand = DocumentType<typeof fragment>;

type ExistingNamedContact =
  | Brand["billingContact"]
  | Brand["ordersLogisticsContact"]
  | undefined;

type ExistingContact =
  | ExistingNamedContact
  | Brand["headquartersContact"]
  | undefined;

type ExistingContactAddress = NonNullable<
  Brand["headquartersContact"]
>["address"];

const contactSchema: yup.ObjectSchema<ContactInputs> = yup.object().shape({
  email: brandCompanyContactSchemaFields.email,
  phone: brandCompanyContactSchemaFields.phone,
  address: brandCompanyContactSchemaFields.address,
});

const namedContactSchema: yup.ObjectSchema<NamedContactInputs> = yup
  .object()
  .shape({
    name: brandCompanyContactSchemaFields.name,
    email: brandCompanyContactSchemaFields.email,
    phone: brandCompanyContactSchemaFields.phone,
    address: brandCompanyContactSchemaFields.address,
  });

const schema: yup.ObjectSchema<Inputs> = yup.object().shape({
  legalName: brandSchemaFields.legalName,
  website: brandSchemaFields.website,
  companyAttributeIds: yup
    .array()
    .of(yup.string().uuid().defined())
    .defined()
    .default([])
    .label("Certifications & Attributes"),
  headquartersContact: contactSchema,
  billingContact: namedContactSchema,
  ordersLogisticsContact: namedContactSchema,
});

const { ControlledFormField, FormField } = createFormFieldComponents<Inputs>({
  schema,
});

const processApolloError = makePostgraphileErrorProcessor<PrimitiveInputs>(
  getPostgraphileErrorMatchersForFields(BRAND_FIELDS, { type: "Brand" }),
);

function getDefaultAddressValues(
  address: ExistingContactAddress | null | undefined,
): AddressFieldsetType {
  return {
    line1: address?.line1 ?? "",
    line2: address?.line2 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? null,
    zip: address?.zip ?? "",
  };
}

function getDefaultContactValues(contact: ExistingContact): ContactInputs {
  return {
    email: contact?.email ?? "",
    phone: contact?.phoneNumber?.phone ?? "",
    address: getDefaultAddressValues(contact?.address),
  };
}

function getDefaultNamedContactValues(
  contact: ExistingNamedContact,
): NamedContactInputs {
  return {
    name: contact?.name ?? "",
    email: contact?.email ?? "",
    phone: contact?.phoneNumber?.phone ?? "",
    address: getDefaultAddressValues(contact?.address),
  };
}

function getDefaultValues(brand: Brand): Inputs {
  return {
    legalName: brand.legalName ?? "",
    website: brand.website ?? "",
    companyAttributeIds: brand.companyAttributesList.map(({ id }) => id),
    headquartersContact: getDefaultContactValues(brand.headquartersContact),
    billingContact: getDefaultNamedContactValues(brand.billingContact),
    ordersLogisticsContact: getDefaultNamedContactValues(
      brand.ordersLogisticsContact,
    ),
  };
}

function getContactMutationData({
  brandId,
  formData,
  includeName,
  existingContact,
}: {
  brandId: UUID;
  formData: ContactInputs | NamedContactInputs;
  includeName: boolean;
  existingContact: ExistingContact;
}) {
  const addressInputData = {
    line1: formData.address.line1,
    line2: formData.address.line2,
    city: formData.address.city,
    state: formData.address.state,
    zip: formData.address.zip,
  };

  let address: AddressInput | null = null;
  let addressId: UUID | null = null;
  if (getHasAddressData(addressInputData)) {
    addressId = existingContact?.addressId ?? uuidv4();
    address = {
      id: addressId,
      ...addressInputData,
    };
  }

  let phoneNumber: PhoneNumberInput | null = null;
  let phoneNumberId: UUID | null = null;
  if (formData.phone) {
    phoneNumberId = existingContact?.phoneNumberId ?? uuidv4();
    phoneNumber = {
      id: phoneNumberId,
      phone: formData.phone,
    };
  }

  let name: string | null = null;
  if (includeName && "name" in formData) {
    name = formData.name;
  }

  if (!(name || formData.email || addressId || phoneNumberId)) {
    return null;
  }

  const contactId = existingContact?.id ?? uuidv4();

  const brandCompanyContact: BrandCompanyContactInput = {
    id: contactId,
    brandId,
    name,
    email: formData.email,
    addressId,
    phoneNumberId,
  };

  return {
    contactId,
    address,
    phoneNumber,
    brandCompanyContact,
  };
}

type Props = {
  brand: FragmentType<typeof fragment> | null | undefined;
  isLoading?: boolean;
  onCancel: () => void;
  onSaved: () => void;
};

export default function BrandSettingsCompanyInformationEdit({
  brand: brandFragmentData,
  isLoading,
  onCancel,
  onSaved,
}: Props) {
  const brand = getFragmentData(fragment, brandFragmentData);
  const brandId = brand?.id;

  const formContext = useForm<Inputs>({
    resolver: yupResolver(schema),
  });

  const {
    formState: { isDirty },
    reset,
  } = formContext;

  const [updateCompanyInformation, { loading: isSaving }] =
    useBrandMutation(mutation);

  useEffect(() => {
    if (brand) {
      reset(getDefaultValues(brand));
    }
  }, [brand, reset]);

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    async (formData: Inputs) => {
      assertBrandId(brandId);

      const headquartersContactData = getContactMutationData({
        brandId,
        formData: formData.headquartersContact,
        includeName: false,
        existingContact: brand?.headquartersContact,
      });

      const billingContactData = getContactMutationData({
        brandId,
        formData: formData.billingContact,
        includeName: true,
        existingContact: brand?.billingContact,
      });

      const ordersLogisticsContactData = getContactMutationData({
        brandId,
        formData: formData.ordersLogisticsContact,
        includeName: true,
        existingContact: brand?.ordersLogisticsContact,
      });

      const contactData = [
        headquartersContactData,
        billingContactData,
        ordersLogisticsContactData,
      ];

      const addresses: AddressInput[] = contactData
        .map((item) => item?.address)
        .filter((item): item is AddressInput => !!item);

      const phoneNumbers: PhoneNumberInput[] = contactData
        .map((item) => item?.phoneNumber)
        .filter((item): item is PhoneNumberInput => !!item);

      const brandCompanyContacts: BrandCompanyContactInput[] = contactData
        .map((item) => item?.brandCompanyContact)
        .filter((item): item is BrandCompanyContactInput => !!item);

      const result = await updateCompanyInformation({
        variables: {
          brandId,
          brandPatch: {
            legalName: formData.legalName,
            website: formData.website,
            headquartersContactId: headquartersContactData?.contactId,
            billingContactId: billingContactData?.contactId,
            ordersLogisticsContactId: ordersLogisticsContactData?.contactId,
          },
          addresses,
          phoneNumbers,
          brandCompanyContacts,
          companyAttributeIds: formData.companyAttributeIds,
        },
      });

      onSaved();

      return result;
    },
    {
      successMessage: "Company information updated successfully.",
      processApolloError,
    },
  );

  const handleCancel = () => {
    if (brand) {
      reset(getDefaultValues(brand));
    }
    onCancel();
  };

  return (
    <Form
      formContext={formContext}
      isLoaded={!!brandId && !isLoading}
      onValidSubmit={handleValidSubmit}
    >
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>
        <Box>
          <Heading as="h3" size="sm" color="magenta.veryDark" mb={2}>
            Company
          </Heading>

          <FormField name="legalName" />
          <ControlledFormField
            name="website"
            renderInput={({ field: props }) => (
              <UrlInput allowPath={false} {...props} />
            )}
          />
          <ControlledFormField
            name="companyAttributeIds"
            isRequired={false}
            renderInput={({ field }) => <CompanyAttributeSelect {...field} />}
          />

          <Box mt={8}>
            <Heading as="h3" size="sm" color="magenta.veryDark" mb={2}>
              Headquarters
            </Heading>
            <FormField name="headquartersContact.email" />
            <ControlledFormField
              name="headquartersContact.phone"
              renderInput={({ field }) => <PhoneInput {...field} />}
            />
            <AddressFieldset<Inputs> fieldNamePrefix="headquartersContact.address" />
          </Box>
        </Box>

        <Box>
          <Box
            p={4}
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="lg"
            bg="white"
          >
            <Heading as="h3" size="sm" color="magenta.veryDark" mb={2}>
              Billing Contact
            </Heading>
            <FormField name="billingContact.name" />
            <FormField name="billingContact.email" />
            <ControlledFormField
              name="billingContact.phone"
              renderInput={({ field }) => <PhoneInput {...field} />}
            />
            <AddressFieldset<Inputs> fieldNamePrefix="billingContact.address" />
          </Box>

          <Box
            mt={6}
            p={4}
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="lg"
            bg="white"
          >
            <Heading as="h3" size="sm" color="magenta.veryDark" mb={2}>
              Orders & Logistics Contact
            </Heading>
            <FormField name="ordersLogisticsContact.name" />
            <FormField name="ordersLogisticsContact.email" />
            <ControlledFormField
              name="ordersLogisticsContact.phone"
              renderInput={({ field }) => <PhoneInput {...field} />}
            />
            <AddressFieldset<Inputs> fieldNamePrefix="ordersLogisticsContact.address" />
          </Box>
        </Box>
      </SimpleGrid>

      <Flex justifyContent="flex-end" gap={2} mt={8}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          colorScheme="pinkButton"
          size="sm"
          disabled={isSaving || !isDirty}
          isLoading={isSaving}
          loadingText="Saving"
        >
          Save Company Information
        </Button>
      </Flex>
    </Form>
  );
}
